import { useEffect, useState } from 'react';

import {
  buildLocalTerminalWsUrl,
  encodeLocalTerminalAuthMessage,
} from './sandbox-terminal-protocol';

/** Plain-text tail kept for the card; the raw buffer is allowed twice this before trimming. */
const TAIL_MAX_CHARS = 4_000;
/** Coalesce bursts of PTY chunks into one render. */
const RENDER_INTERVAL_MS = 80;

// CSI / OSC / charset / keypad sequences, plus stray C0 controls (keeps \t \n \r).
const ANSI_RE =
  /\u001b\[[0-9;?]*[ -/]*[@-~]|\u001b\][^\u0007\u001b]*(?:\u0007|\u001b\\)|\u001b[()][A-Za-z0-9]|\u001b[=>]|[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/g;

/**
 * Terminal output as plain text: escape sequences stripped, `\r\n` normalized,
 * and a bare `\r` treated as "overwrite this line" (progress bars, spinners) by
 * keeping only what came after the last one. Trailing whitespace trimmed.
 */
export function plainTail(raw: string, maxChars = TAIL_MAX_CHARS): string {
  const text = raw
    .replace(ANSI_RE, '')
    .replace(/\r\n/g, '\n')
    .split('\n')
    .map((line) => {
      const cr = line.lastIndexOf('\r');
      return cr === -1 ? line : line.slice(cr + 1);
    })
    .join('\n')
    .trimEnd();
  return text.length > maxChars ? text.slice(-maxChars) : text;
}

/**
 * Live tail of a running agent command: attaches a second, observe-only
 * websocket to its host session (cols/rows 0 so the host never resizes the PTY
 * under the command) and returns the trailing output as plain text. Stops when
 * `live` turns false or the session closes. The host replays the buffer on
 * attach, so a card that mounts mid-run still sees earlier output.
 */
export function useAgentRunTail(
  sessionId: string | null,
  live: boolean
): string {
  const [tail, setTail] = useState('');

  useEffect(() => {
    if (!sessionId || !live) return;
    let socket: WebSocket | null = null;
    let cancelled = false;
    let raw = '';
    let flushTimer: ReturnType<typeof setTimeout> | null = null;
    const decoder = new TextDecoder();

    const flush = () => {
      flushTimer = null;
      if (!cancelled) setTail(plainTail(raw));
    };

    // Lazy: this hook is reached from the chat's modality registry, which must
    // stay importable outside Electron (tests, web) where electron-api is not.
    import('@/features/electron-api')
      .then(({ electronApis }) => electronApis.localTerminal.getEndpoint())
      .then((endpoint) => {
        if (cancelled || !endpoint) return;
        socket = new WebSocket(buildLocalTerminalWsUrl(endpoint.port, 0, 0));
        socket.binaryType = 'arraybuffer';
        socket.onopen = () =>
          socket?.send(
            encodeLocalTerminalAuthMessage(endpoint.token, 0, 0, sessionId, {
              attachOnly: true,
            })
          );
        socket.onmessage = (event: MessageEvent<string | ArrayBuffer>) => {
          if (typeof event.data === 'string') return; // control frames
          raw += decoder.decode(event.data, { stream: true });
          if (raw.length > TAIL_MAX_CHARS * 2) raw = raw.slice(-TAIL_MAX_CHARS);
          flushTimer ??= setTimeout(flush, RENDER_INTERVAL_MS);
        };
        socket.onclose = () => {
          if (flushTimer) clearTimeout(flushTimer);
          flush();
        };
      })
      .catch(() => {
        // no host, no tail; the card still shows the final result when it lands
      });

    return () => {
      cancelled = true;
      if (flushTimer) clearTimeout(flushTimer);
      socket?.close();
    };
  }, [sessionId, live]);

  return tail;
}
